import { useState, useCallback } from "react";
import { getOneOmsBaseUrl } from "../constants/omsRegionConfig";
import tokenUtils from "../utils/tokenUtils";

const CANCELLATION_ACTION = {
  accept: "APPROVED",
  reject: "REJECTED",
};

const callCancellationApi = async (action, orderId) => {
  const token = tokenUtils.getToken();
  const actionParam = CANCELLATION_ACTION[action];
  const endpoint = `${getOneOmsBaseUrl()}/update-order-cancellation-request/${orderId}/${actionParam}`;

  const headers = {
    accept: "application/json",
    "accept-language": "en-US,en;q=0.7",
    "cache-control": "no-cache",
    "content-type": "application/json",
    "content-length": "0",
    origin: "https://adminsupport.infinitylearn.com",
    pragma: "no-cache",
    priority: "u=1, i",
    referer: "https://adminsupport.infinitylearn.com/",
    "auth-token": token || "",
  };

  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers,
    });

    if (!response.ok) {
      const errorJson = await response.json().catch(() => ({}));
      const errorMsg =
        errorJson?.message ||
        errorJson?.detail ||
        `Failed to ${actionParam} the cancellation request. Please try again.`;
      throw new Error(errorMsg);
    }

    const text = await response.text();
    return text ? JSON.parse(text) : { success: true };
  } catch (err) {
    throw err;
  }
};

export const useOrderCancellationActions = ({ onSuccess, onError } = {}) => {
  const [loadingMap, setLoadingMap] = useState({});

  const setOrderLoading = (orderId, actionType) =>
    setLoadingMap((prev) => ({ ...prev, [orderId]: actionType }));

  const handleAction = useCallback(
    async (action, order) => {
      const { order_id } = order;
      setOrderLoading(order_id, action);

      try {
        const result = await callCancellationApi(action, order_id);
        onSuccess?.(action, order, result);
      } catch (err) {
        console.error(`[useOrderCancellationActions] ${action} failed:`, err);
        onError?.(action, order, err.message);
      } finally {
        setOrderLoading(order_id, null);
      }
    },
    [onSuccess, onError],
  );

  const acceptCancellation = useCallback(
    (order) => handleAction("accept", order),
    [handleAction],
  );

  const rejectCancellation = useCallback(
    (order) => handleAction("reject", order),
    [handleAction],
  );

  const isOrderLoading = (orderId) => loadingMap[orderId] || null;

  return { acceptCancellation, rejectCancellation, isOrderLoading };
};
